// Líneas y paros — la pantalla del TÉCNICO de mantenimiento
// (/mantenimiento/lineas):
//
//   · Sección 1: grid de líneas en vivo (espejo del S1 del supervisor, misma
//     celda: border-top por semáforo 90/70, EstadoPill de paro/captura) +
//     chips de paros del día y pendientes de validación. Click → drawer con
//     el detalle de paros del día y sus acciones.
//   · Sección 2: cola de validación (paros cerrados sin sello de mtto) con
//     toggles "Solo mantenimiento" / "Incluir validados" y las MISMAS
//     acciones por fila (reusa ParoAcciones/modales del drawer).
//
// El gate duro es el API: acá `puedeOperar` solo muestra/oculta acciones.

import { useState } from 'react';
import type { LineaEstadoMtto, SupervisorKpiLinea, TurnoObjetivo } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Chip } from '../../components/ui/chip';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { semaforoCumplimiento, toneTextClass } from '../../lib/semaforo';
import { cn } from '../../lib/utils';
import { etiquetaLider } from '../supervisor/etiquetas';
import { useEstructuraMtto } from './estructura';
import { CargandoModulo, ErrorModulo, MantenimientoShell } from './MantenimientoShell';
import {
  EstadoParoMttoBadge,
  ParoAcciones,
  ParoDetalleDrawer,
  fmtHoraParo,
  fmtMinutosParo,
  type LineaSeleccionada,
} from './ParoDetalleDrawer';
import { useLineasEstadoMtto, useParosPendientesMtto } from './queries';
import { BannerSinRolMtto, fmtFecha, usePermisosMtto } from './shared';

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export function LineasParosMttoPage() {
  const { puedeOperar } = usePermisosMtto();
  const estructura = useEstructuraMtto();

  const [plantaId, setPlantaId] = useState('');
  const [lineaSel, setLineaSel] = useState<LineaSeleccionada | null>(null);
  const [soloMantenimiento, setSoloMantenimiento] = useState(true);
  const [incluirValidados, setIncluirValidados] = useState(false);

  const lineasQ = useLineasEstadoMtto(plantaId || undefined);
  const pendientesQ = useParosPendientesMtto({
    solo_mantenimiento: soloMantenimiento,
    incluir_validados: incluirValidados,
    ...(plantaId ? { planta_id: plantaId } : {}),
  });

  const data = lineasQ.data;
  const lineasConKpi = data?.lineas.filter((l) => l.kpi !== null) ?? [];
  const lineasSinKpi = data?.lineas.filter((l) => l.kpi === null) ?? [];

  return (
    <MantenimientoShell
      titulo="Líneas y paros"
      subtitulo="Estado en vivo de las líneas y validación de los paros del piso de planta"
    >
      <div className="flex flex-col gap-5">
        {/* ============================================================ */}
        {/* Barra: selector de planta + turno objetivo + hora servidor   */}
        {/* ============================================================ */}
        <section className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 rounded-md border border-border bg-surface-base p-3">
          <div className="flex flex-col gap-1">
            <Label
              htmlFor="lineas-planta"
              className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary"
            >
              Planta
            </Label>
            <Select
              id="lineas-planta"
              className="h-8 min-w-[180px] py-0 text-xs"
              value={plantaId}
              disabled={!estructura.disponible}
              onChange={(e) => setPlantaId(e.target.value)}
            >
              <option value="">Todas las plantas</option>
              {estructura.plantas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </Select>
          </div>
          {data ? (
            <TurnoObjetivoInfo
              turno={data.turno_objetivo}
              horaServidor={data.hora_servidor}
              fechaHoy={data.fecha_hoy}
            />
          ) : null}
        </section>

        {/* ============================================================ */}
        {/* Sección 1: grid de líneas en vivo                            */}
        {/* ============================================================ */}
        <section className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-[11px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Estado de líneas en vivo
            </h2>
            {lineasQ.isFetching && !lineasQ.isLoading ? (
              <p className="text-[11px] text-fg-tertiary">actualizando…</p>
            ) : null}
          </div>

          {lineasQ.isLoading ? <CargandoModulo mensaje="Cargando estado de líneas…" /> : null}
          {lineasQ.isError ? <ErrorModulo error={lineasQ.error} /> : null}

          {data && data.lineas.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-fg-tertiary">
                Sin líneas activas para el filtro seleccionado.
              </CardContent>
            </Card>
          ) : null}

          {data && data.lineas.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[...lineasConKpi, ...lineasSinKpi].map((item) => (
                <LineaCardMtto
                  key={item.linea.id}
                  item={item}
                  onClick={() => setLineaSel({ id: item.linea.id, nombre: item.linea.nombre })}
                />
              ))}
            </div>
          ) : null}
        </section>

        {/* ============================================================ */}
        {/* Sección 2: cola de validación                                */}
        {/* ============================================================ */}
        <section className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[11px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Cola de validación
              </h2>
              {pendientesQ.data ? (
                <>
                  <Chip
                    tone={pendientesQ.data.conteos.pendientes > 0 ? 'warn' : 'ok'}
                    className="px-2 py-0.5 text-[11px]"
                  >
                    {pendientesQ.data.conteos.pendientes} pendiente(s)
                  </Chip>
                  <Badge tone="info">
                    {pendientesQ.data.conteos.con_correccion_propuesta} con corrección propuesta
                  </Badge>
                  <span className="text-[11px] text-fg-tertiary">
                    {fmtFecha(pendientesQ.data.rango.desde)} →{' '}
                    {fmtFecha(pendientesQ.data.rango.hasta)}
                  </span>
                </>
              ) : null}
            </div>
            <div className="flex items-center gap-3">
              <ToggleCheckbox
                id="cola-solo-mtto"
                label="Solo mantenimiento"
                checked={soloMantenimiento}
                onChange={setSoloMantenimiento}
              />
              <ToggleCheckbox
                id="cola-incluir-validados"
                label="Incluir validados"
                checked={incluirValidados}
                onChange={setIncluirValidados}
              />
            </div>
          </div>

          {!puedeOperar ? <BannerSinRolMtto accion="validar o corregir paros" /> : null}

          {soloMantenimiento &&
          pendientesQ.data &&
          !pendientesQ.data.filtro_mantenimiento_aplicado ? (
            <p className="text-[11px] text-warn">
              El catálogo no tiene categorías de mantenimiento marcadas — se muestran todos los
              paros del rango.
            </p>
          ) : null}

          {pendientesQ.isLoading ? <CargandoModulo mensaje="Cargando cola de validación…" /> : null}
          {pendientesQ.isError ? <ErrorModulo error={pendientesQ.error} /> : null}

          {pendientesQ.data && pendientesQ.data.items.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-fg-tertiary">
                {incluirValidados
                  ? 'Sin paros en el rango con los filtros actuales.'
                  : 'Sin paros pendientes de validación — al día.'}
              </CardContent>
            </Card>
          ) : null}

          {pendientesQ.data && pendientesQ.data.items.length > 0 ? (
            <Table maxHeight="60vh">
              <THead sticky>
                <TR>
                  <TH>Fecha</TH>
                  <TH>Línea</TH>
                  <TH>Turno</TH>
                  <TH>Causal</TH>
                  <TH align="center">Horario</TH>
                  <TH align="right">Min</TH>
                  <TH>Registró</TH>
                  <TH align="center">Estado</TH>
                  <TH>Acciones</TH>
                </TR>
              </THead>
              <TBody>
                {pendientesQ.data.items.map((p) => (
                  <TR key={p.id}>
                    <TD className="whitespace-nowrap text-xs tabular-nums text-fg-secondary">
                      {fmtFecha(p.fecha)}
                    </TD>
                    <TD className="text-xs">
                      <span className="font-medium text-fg">{p.linea_nombre}</span>
                      <span className="block text-[10px] text-fg-tertiary">{p.planta_nombre}</span>
                    </TD>
                    <TD className="whitespace-nowrap text-xs text-fg-secondary">
                      {p.turno_nombre ?? (p.turno_numero !== null ? `T${p.turno_numero}` : '—')}
                    </TD>
                    <TD className="max-w-[260px] text-xs text-fg">
                      <span className="block truncate">
                        {p.causal_madre.nombre} → {p.causal.nombre}
                      </span>
                      {p.categoria.es_mantenimiento ? (
                        <span className="text-[10px] text-accent">{p.categoria.nombre}</span>
                      ) : (
                        <span className="text-[10px] text-fg-tertiary">{p.categoria.nombre}</span>
                      )}
                    </TD>
                    <TD align="center" className="whitespace-nowrap text-xs tabular-nums">
                      {fmtHoraParo(p.hora_inicio)} —{' '}
                      {p.hora_fin === null ? (
                        <span className="font-medium text-crit">en curso</span>
                      ) : (
                        fmtHoraParo(p.hora_fin)
                      )}
                    </TD>
                    <TD align="right" numeric className="whitespace-nowrap text-xs text-fg">
                      {fmtMinutosParo(p.minutos_perdidos)}
                    </TD>
                    <TD className="whitespace-nowrap text-xs text-fg-secondary">
                      {p.lider ? `${p.lider.nombre} ${p.lider.apellido}` : '—'}
                    </TD>
                    <TD align="center">
                      <EstadoParoMttoBadge estado={p.estado_mtto} />
                    </TD>
                    <TD>
                      <ParoAcciones paro={p} puedeOperar={puedeOperar} />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : null}
        </section>
      </div>

      <ParoDetalleDrawer linea={lineaSel} onClose={() => setLineaSel(null)} />
    </MantenimientoShell>
  );
}

// ---------------------------------------------------------------------------
// Turno objetivo + hora servidor (header de la sección en vivo)
// ---------------------------------------------------------------------------

const FUENTE_TURNO_LABEL: Record<TurnoObjetivo['fuente'], string> = {
  programacion_hoy: 'según programación de hoy',
  turno_actual: 'según reloj del servidor (sin programación)',
  sin_turno: 'fuera de turno',
};

function TurnoObjetivoInfo({
  turno,
  horaServidor,
  fechaHoy,
}: {
  turno: TurnoObjetivo;
  horaServidor: string;
  fechaHoy: string;
}) {
  const nombre =
    turno.nombre ?? (turno.numero !== null ? `Turno ${turno.numero}` : 'Sin turno activo');
  return (
    <div className="flex flex-col items-end gap-0.5 text-right">
      <p className="text-sm text-fg">
        <span className="font-medium">{nombre}</span>
        {turno.hora_inicio && turno.hora_fin ? (
          <span className="text-fg-secondary">
            {' '}
            · {turno.hora_inicio} — {turno.hora_fin}
          </span>
        ) : null}
        <span className="text-fg-tertiary"> · {FUENTE_TURNO_LABEL[turno.fuente]}</span>
      </p>
      <p className="text-[11px] text-fg-tertiary">
        {fmtFecha(fechaHoy)} · hora del servidor{' '}
        <span className="font-mono">{horaServidor.slice(0, 5)}</span>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Celda de línea — clon de la LineaCard del S1 del supervisor + chips de paros
// ---------------------------------------------------------------------------

export function LineaCardMtto({ item, onClick }: { item: LineaEstadoMtto; onClick: () => void }) {
  const { linea, kpi, paros_hoy_count, paros_pendientes_count } = item;
  const topBorderClass = topBorderForSemaforo(kpi);

  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full text-left transition-transform hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <Card>
        <div className={cn('h-1 w-full', topBorderClass)} />
        <CardContent className="flex flex-col gap-2 p-4 pt-3 text-sm">
          {/* Header: nombre + código */}
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="truncate text-sm font-medium text-fg">{linea.nombre}</h3>
            <span className="text-[10px] text-fg-tertiary">{linea.codigo}</span>
          </div>

          {/* Líder */}
          {kpi?.lider ? (
            <p className="-mt-1 truncate text-[11px] text-fg-tertiary">
              {etiquetaLider(kpi.lider.nombre, kpi.lider.apellido)}
            </p>
          ) : (
            <p className="-mt-1 text-[11px] text-fg-tertiary">Sin líder asignado</p>
          )}

          {/* Cuerpo: cumplimiento gigante + subtítulo */}
          {kpi === null ? (
            <div className="py-2">
              <p className="text-3xl font-medium leading-none text-fg-tertiary">—</p>
              <p className="mt-1 text-[11px] text-fg-tertiary">Sin captura aún hoy</p>
            </div>
          ) : kpi.estado_semaforo === 'sin_data' || kpi.cumplimiento_pct === null ? (
            <div className="py-2">
              <p
                className="text-3xl font-medium leading-none text-fg-secondary"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatearMiles(kpi.cantidad_real)}
              </p>
              <p className="mt-1 text-[11px] text-fg-tertiary">
                paq · {kpi.bloques_capturados} bloque(s)
              </p>
              <p className="mt-1 text-[11px] text-warn">⚠ Falta estándar para este SKU</p>
            </div>
          ) : (
            <div className="py-1">
              <p
                className={cn(
                  'text-4xl font-medium leading-none',
                  toneTextClass(semaforoCumplimiento(kpi.cumplimiento_pct)),
                )}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {Math.round(kpi.cumplimiento_pct)}%
              </p>
              <p className="mt-1 truncate text-[11px] text-fg-tertiary">
                <span className="tabular-nums">{formatearMiles(kpi.cantidad_real)}</span> paq
                {kpi.sku_predominante ? <> · SKU {kpi.sku_predominante.codigo_cliente}</> : null}
              </p>
            </div>
          )}

          {/* Estado-pill al pie */}
          <EstadoPill kpi={kpi} />

          {/* Chips de paros del día + pendientes de validación */}
          <div className="flex flex-wrap items-center gap-1.5 border-t border-border pt-2">
            <Chip className="px-2 py-0.5 text-[11px]">
              {paros_hoy_count} paro{paros_hoy_count === 1 ? '' : 's'} hoy
            </Chip>
            {paros_pendientes_count > 0 ? (
              <Badge tone="warn">{paros_pendientes_count} por validar</Badge>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </button>
  );
}

/**
 * Border-top de color (clon del S1): paro_activo → crit; sin data → neutral;
 * si no, el semáforo unificado 90/70 sobre cumplimiento_pct.
 */
function topBorderForSemaforo(kpi: SupervisorKpiLinea | null): string {
  if (kpi === null) return 'bg-border';
  if (kpi.paro_activo) return 'bg-crit';
  if (kpi.estado_semaforo === 'sin_data' || kpi.cumplimiento_pct === null) return 'bg-border';
  switch (semaforoCumplimiento(kpi.cumplimiento_pct)) {
    case 'crit':
      return 'bg-crit';
    case 'warn':
      return 'bg-warn';
    case 'ok':
      return 'bg-ok';
    default:
      return 'bg-border';
  }
}

/**
 * Estado-pill contextual (clon del S1, sin `programacionActiva` — el endpoint
 * de mtto no la expone): paro activo → crit; sin captura ≥70 min → warn;
 * captura reciente → ok; sin captura aún → neutral.
 */
function EstadoPill({ kpi }: { kpi: SupervisorKpiLinea | null }) {
  if (kpi === null) {
    return <PillBase tone="neutral">Sin captura aún</PillBase>;
  }
  if (kpi.paro_activo) {
    const min = kpi.paro_activo_minutos ?? 0;
    const causal = kpi.paro_activo_causal ?? 'sin causal';
    return (
      <PillBase tone="crit">
        ⏵ Paro {formatMinutosCortos(min)} · {causal}
      </PillBase>
    );
  }
  if (kpi.estado_semaforo === 'amarillo' && kpi.cumplimiento_pct !== null) {
    return <PillBase tone="warn">Baja capacidad · {Math.round(kpi.cumplimiento_pct)}%</PillBase>;
  }
  if (kpi.minutos_desde_ultima_captura !== null) {
    const min = kpi.minutos_desde_ultima_captura;
    if (min >= 70) {
      return <PillBase tone="warn">Sin captura {formatMinutosCortos(min)}</PillBase>;
    }
    if (kpi.ultimo_bloque !== null) {
      return <PillBase tone="ok">Captura ok · hace {min} min</PillBase>;
    }
  }
  return null;
}

function PillBase({
  tone,
  children,
}: {
  tone: 'ok' | 'warn' | 'crit' | 'neutral';
  children: React.ReactNode;
}) {
  const cls = {
    ok: 'bg-ok-soft text-ok',
    warn: 'bg-warn-soft text-warn',
    crit: 'bg-crit-soft text-crit',
    neutral: 'bg-surface-overlay text-fg-tertiary',
  }[tone];
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        cls,
      )}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ToggleCheckbox({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer select-none items-center gap-1.5 text-xs text-fg-secondary"
    >
      <input
        id={id}
        type="checkbox"
        className="size-3.5"
        style={{ accentColor: 'var(--color-accent)' }}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

/** "45m" / "1h 12m" — mismo formato corto del S1 para los pills. */
function formatMinutosCortos(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function formatearMiles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(n);
}
